import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import StatusPill from "@/components/StatusPill";
import { DeliveryPill } from "@/components/AdminDistribution";
import { CaretLeft, CaretRight, SquaresFour, ListBullets, ArrowUpRight } from "@phosphor-icons/react";

const BASE = API.replace(/\/api$/, "");
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DELIVERY_DOT = { live: "bg-green-500", submitted: "bg-blue-400", delivering: "bg-yellow-500", failed: "bg-red-600" };
const pad = (n) => String(n).padStart(2, "0");
const ym = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;

export default function ReleaseCalendar({ title = "Release Calendar" }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [data, setData] = useState({ releases: [], can_reschedule: false });
  const [view, setView] = useState("grid");
  const [selectedDay, setSelectedDay] = useState(null);
  const [dragId, setDragId] = useState(null);
  const month = ym(cursor);

  const load = useCallback(() => api.get("/calendar", { params: { month } }).then(({ data }) => setData(data)).catch(() => {}), [month]);
  useEffect(() => { load(); setSelectedDay(null); }, [load]);

  const byDay = useMemo(() => data.releases.reduce((acc, r) => { (acc[r.release_date] ||= []).push(r); return acc; }, {}), [data]);
  const cells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7;
    const days = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const out = Array(offset).fill(null);
    for (let d = 1; d <= days; d++) out.push(`${month}-${pad(d)}`);
    while (out.length % 7) out.push(null);
    return out;
  }, [cursor, month]);
  const today = `${new Date().getFullYear()}-${pad(new Date().getMonth() + 1)}-${pad(new Date().getDate())}`;

  const reschedule = async (id, date) => {
    const r = data.releases.find(x => x.id === id);
    if (!r || r.release_date === date) return;
    try {
      await api.patch(`/admin/releases/${id}/schedule`, { release_date: date });
      toast.success(`"${r.title}" moved to ${date}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reschedule"); }
  };

  const shift = (n) => setCursor(c => new Date(c.getFullYear(), c.getMonth() + n, 1));
  const label = cursor.toLocaleString("en-US", { month: "long", year: "numeric" });

  return (
    <section data-testid="release-calendar">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-2xl uppercase tracking-tight">{title}</h2>
          <p className="text-zinc-500 text-xs mt-1">{data.releases.length} release{data.releases.length === 1 ? "" : "s"} in {label}{data.can_reschedule ? " · drag a release to reschedule" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-zinc-800">
            <button onClick={() => setView("grid")} data-testid="calendar-view-grid" className={`px-3 py-2 ${view === "grid" ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}><SquaresFour size={14} /></button>
            <button onClick={() => setView("agenda")} data-testid="calendar-view-agenda" className={`px-3 py-2 border-l border-zinc-800 ${view === "agenda" ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}><ListBullets size={14} /></button>
          </div>
          <div className="flex items-center border border-zinc-800">
            <button onClick={() => shift(-1)} data-testid="calendar-prev" className="px-3 py-2 text-zinc-400 hover:text-white"><CaretLeft size={14} /></button>
            <span className="font-display text-sm uppercase tracking-tight px-3 min-w-[160px] text-center" data-testid="calendar-month-label">{label}</span>
            <button onClick={() => shift(1)} data-testid="calendar-next" className="px-3 py-2 text-zinc-400 hover:text-white"><CaretRight size={14} /></button>
          </div>
        </div>
      </div>

      {view === "grid" ? (
        <div className="grid lg:grid-cols-12 border border-zinc-800">
          <div className="lg:col-span-9 border-r border-zinc-800">
            <div className="grid grid-cols-7 border-b border-zinc-800 bg-zinc-950">{DAYS.map(d => <div key={d} className="px-2 py-2 text-[10px] tracking-[0.2em] uppercase text-zinc-500">{d}</div>)}</div>
            <div className="grid grid-cols-7" data-testid="calendar-grid">
              {cells.map((date, i) => {
                const items = date ? byDay[date] || [] : [];
                const sel = selectedDay === date;
                return (
                  <div key={date || `pad-${i}`} data-testid={date ? `calendar-day-${date}` : undefined}
                    onClick={() => date && setSelectedDay(date)}
                    onDragOver={(e) => { if (data.can_reschedule && dragId && date) e.preventDefault(); }}
                    onDrop={(e) => { e.preventDefault(); if (dragId && date) reschedule(dragId, date); setDragId(null); }}
                    className={`min-h-[92px] p-1.5 border-r border-b border-zinc-800 [&:nth-child(7n)]:border-r-0 transition-colors ${!date ? "bg-zinc-950/60" : "cursor-pointer hover:bg-zinc-900/60"} ${sel ? "bg-red-600/10" : ""} ${dragId && date ? "outline-dashed outline-1 -outline-offset-2 outline-zinc-700" : ""}`}>
                    {date && <div className={`text-[10px] font-mono mb-1 ${date === today ? "text-red-500 font-bold" : "text-zinc-500"}`}>{Number(date.slice(-2))}</div>}
                    <div className="space-y-1">
                      {items.slice(0, 3).map(r => (
                        <div key={r.id} draggable={data.can_reschedule && r.status !== "live" && r.status !== "takedown"} data-testid={`calendar-chip-${r.id}`}
                          onDragStart={() => setDragId(r.id)} onDragEnd={() => setDragId(null)}
                          className={`flex items-center gap-1.5 bg-zinc-900 border border-zinc-800 px-1.5 py-1 ${data.can_reschedule && r.status !== "live" ? "cursor-grab active:cursor-grabbing" : ""}`}>
                          <img src={`${BASE}${r.cover_url}`} alt="" className="w-5 h-5 object-cover grayscale shrink-0" />
                          <span className="text-[10px] truncate flex-1">{r.title}</span>
                          <span className={`w-1.5 h-1.5 shrink-0 ${DELIVERY_DOT[r.delivery?.status] || "bg-zinc-600"}`} title={r.delivery?.status || "not delivered"} />
                        </div>
                      ))}
                      {items.length > 3 && <div className="text-[9px] text-zinc-500 px-1">+{items.length - 3} more</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="lg:col-span-3 p-5" data-testid="calendar-day-panel">
            {!selectedDay ? (
              <div className="text-xs text-zinc-600 leading-relaxed">
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-3">Legend</div>
                {[["bg-green-500", "Delivered / live in stores"], ["bg-blue-400", "In Too Lost QC"], ["bg-yellow-500", "Delivering"], ["bg-red-600", "Delivery failed"], ["bg-zinc-600", "Not delivered yet"]].map(([c, l]) => (
                  <div key={l} className="flex items-center gap-2 py-1"><span className={`w-2 h-2 ${c}`} />{l}</div>
                ))}
                <p className="mt-4">Click a day to see its drops.</p>
              </div>
            ) : (
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-3">{new Date(`${selectedDay}T00:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}</div>
                {(byDay[selectedDay] || []).length === 0 ? <div className="text-xs text-zinc-600">No releases scheduled.</div> : (byDay[selectedDay] || []).map(r => <DayItem key={r.id} r={r} />)}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="border border-zinc-800" data-testid="calendar-agenda">
          {Object.keys(byDay).length === 0 ? <div className="p-10 text-center text-zinc-500 text-sm">No releases scheduled in {label}.</div> : Object.keys(byDay).sort().map(date => (
            <div key={date} className="grid grid-cols-12 border-b border-zinc-800 last:border-b-0">
              <div className="col-span-2 p-4 border-r border-zinc-800 bg-zinc-950">
                <div className={`font-display text-3xl tracking-tighter ${date === today ? "text-red-500" : ""}`}>{Number(date.slice(-2))}</div>
                <div className="text-[10px] tracking-widest uppercase text-zinc-500">{new Date(`${date}T00:00:00`).toLocaleDateString("en-US", { weekday: "short" })}</div>
              </div>
              <div className="col-span-10 divide-y divide-zinc-800">{byDay[date].map(r => <div key={r.id} className="p-4"><DayItem r={r} /></div>)}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function DayItem({ r }) {
  return (
    <div className="flex items-start gap-3 py-2" data-testid={`calendar-item-${r.id}`}>
      <img src={`${BASE}${r.cover_url}`} alt="" className="w-12 h-12 object-cover grayscale shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{r.title}</div>
        <div className="text-xs text-zinc-400 truncate">{r.artist_name} · {r.release_type} · {r.track_count} trk</div>
        <div className="flex flex-wrap items-center gap-2 mt-2"><StatusPill status={r.status} /><DeliveryPill delivery={r.delivery} /></div>
      </div>
      {r.presave_slug && <Link to={`/presave/${r.presave_slug}`} className="text-zinc-500 hover:text-white shrink-0"><ArrowUpRight size={14} /></Link>}
    </div>
  );
}
